<template>
  <div class="swimlanes-wrap">
    <div class="flex flex-row sticky top-0 z-[11] pt-2 bg-background-strongest">
      <div
        v-for="(mainList, index) in filteredMainListIds"
        :key="index"
        class="flex flex-row"
        :class="{
          'empty-group': isMainListEmpty(mainList),
        }"
      >
        <BoardListHeader
          :list-id="mainList"
          :header-footer-style="{}"
          :expandable="false"
          :show-status-icon-picker="showStatusIconPickerMainGroup || isUnassignedListId(mainList)"
          :disable-status-icon-picker="true"
          :group-by="boardGroupBy"
          :list-cards="allListCards.mainLists?.[mainList]?.allCards"
          :visible="true"
          :is-cards-layout-timeline="false"
          :is-epic-board="false"
          :is-main-group="true"
        />
      </div>
    </div>
    <div class="child-lists flex flex-col">
      <div
        v-for="(subGroupList, index) in filteredSubGroupListOrder"
        :key="subGroupList"
        v-observe-visibility="{
          callback: (isVisible: boolean) => {
            if (isVisible) {
              addVisibleSubGroupListId(subGroupList);
            } else {
              removeVisibleSubGroupListId(subGroupList);
            }
          },
        }"
        class="flex flex-col subgroup-wrap"
        :style="computedStyle(subGroupList)"
      >
        <SwimlanesList
          v-if="index < 4 || visibleSubGroupListIds.includes(subGroupList)"
          :list-id="subGroupList"
          :main-list-ids="filteredMainListIds"
          :position="props.position"
          :container-width="props.containerWidth"
          :empty-main-lists="emptyMainListIds"
          :all-list-cards="allListCards"
          :sub-group-cards="subGroupByObject"
          @update:height="updateListsHeights(subGroupList, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import useSwimlanes from '../../boards/composables/useSwimlanes';
import BoardListHeader from './BoardListHeader.vue';
import SwimlanesList from './SwimlanesList.vue';

interface MainListEntry {
  allCards: string[];
  [subGroupListId: string]: string[];
}

interface SwimlanesData {
  mainLists: Record<string, MainListEntry>;
  subgroupsLargestColumns: Record<string, number>;
  subGroups: Record<string, string[]>;
}

const store = useStore();

const { currentBoardId, boardGroupBy, showStatusIconPickerMainGroup, hideEmptyGroups } =
  useSwimlanes();

const props = defineProps({
  mainListIds: {
    type: Array as () => string[],
    required: true,
  },
  position: {
    type: Number,
    required: false,
    default: 0,
  },
  containerWidth: {
    type: Number,
    required: false,
    default: 0,
  },
});

const visibleSubGroupListIds = ref<string[]>([]);

const addVisibleSubGroupListId = (listId: string) => {
  if (!visibleSubGroupListIds.value.includes(listId)) {
    visibleSubGroupListIds.value.push(listId);
  }
};

const removeVisibleSubGroupListId = (listId: string) => {
  const index = visibleSubGroupListIds.value.indexOf(listId);
  if (index > -1) {
    visibleSubGroupListIds.value.splice(index, 1);
  }
};

const subGroupByObject = computed(() => {
  const groupBy = store.getters.getBoardGroupBy(currentBoardId.value);
  const subGroupBy = store.getters.getBoardSubGroupBy(currentBoardId.value);
  return store.getters.getBoardSubGroupByObject(currentBoardId.value, groupBy, subGroupBy)?.[
    groupBy
  ]?.[subGroupBy];
});

const allListCards = computed<SwimlanesData>(() => {
  const result: SwimlanesData = {
    mainLists: {},
    subgroupsLargestColumns: {},
    subGroups: {},
  };

  const subGroupsObject = subGroupByObject.value;
  for (const mainListId of props.mainListIds) {
    result.mainLists[mainListId] = {
      allCards: [],
    };

    for (const subGroupListId of subGroupListOrder.value) {
      const cardIds = subGroupsObject[mainListId]?.[subGroupListId]?.cardIds ?? [];

      result.mainLists[mainListId].allCards.push(...cardIds);
      if (!result.subGroups[subGroupListId]) {
        result.subGroups[subGroupListId] = [];
      }
      result.subGroups[subGroupListId].push(...cardIds);
      if (
        cardIds.length > 0 &&
        (result.subgroupsLargestColumns[subGroupListId] ?? 0) < cardIds.length
      ) {
        result.subgroupsLargestColumns[subGroupListId] = cardIds.length;
      }
    }
  }
  return result;
});

const listExpanded = (listId: string) => {
  return store.getters.getListExpanded(currentBoardId.value, listId);
};

// Check if a main list is empty (has no cards)
const isMainListEmpty = (mainListId: string) => {
  if (!hideEmptyGroups.value) {
    return false;
  }
  const cards = (allListCards.value.mainLists[mainListId]?.allCards ?? []) as string[];
  return cards.length === 0;
};

const subGroupListOrder = computed(() => {
  return subGroupByObject.value?.order ?? [];
});

const filteredSubGroupListOrder = computed(() => {
  if (!hideEmptyGroups.value) {
    return subGroupListOrder.value;
  }
  return subGroupListOrder.value.filter((listId: string) => {
    return allListCards.value.subgroupsLargestColumns[listId];
  });
});

const filteredMainListIds = computed(() => {
  if (!hideEmptyGroups.value) {
    return props.mainListIds;
  }
  return props.mainListIds.filter((listId) => {
    return !isMainListEmpty(listId);
  });
});

const isUnassignedListId = (listId: string | number) => {
  return listId === `${currentBoardId.value}-unassigned`;
};

// Computed property to get the list of empty main list IDs
const emptyMainListIds = computed(() => {
  if (!hideEmptyGroups.value) {
    return [];
  }

  return (props.mainListIds as Array<string>)
    .filter((mainListId) => {
      return isMainListEmpty(mainListId);
    })
    .map((id) => String(id));
});

const HEADER_HEIGHT = 47; // px
const HEADER_MARGIN_BOTTOM = 12; // px
const CARD_HEIGHT = 102; // px
const CARD_MARGIN_BOTTOM = 8; // px  (vertical spacing between cards)
const BUTTON_HEIGHT = 35; // px  (the "add-card" button incl. its margin)
const HEADER_SECTION = HEADER_HEIGHT + HEADER_MARGIN_BOTTOM;
const CARDS_SECTION = CARD_HEIGHT + CARD_MARGIN_BOTTOM;

const memoizedHeights = new Map<number, number>();

const getWrapperHeight = (cardCount: number, includeButton = true) => {
  if (memoizedHeights.has(cardCount)) {
    return memoizedHeights.get(cardCount)!;
  }
  const cardsSection = cardCount * CARDS_SECTION;
  const buttonSection = includeButton ? BUTTON_HEIGHT : 0;

  const height = HEADER_SECTION + cardsSection + buttonSection;
  memoizedHeights.set(cardCount, height);
  return height;
};

const listsHeights = ref<Record<string, number>>({});

const updateListsHeights = (listId: string, height: number) => {
  if (height > getWrapperHeight(Number(allListCards.value.subgroupsLargestColumns[listId] ?? 0))) {
    listsHeights.value[listId] = height;
  }
};

const MIN_LIST_HEIGHT = 333;

const computedStyle = (listId: string) => {
  if (!listExpanded(listId)) {
    return {
      minHeight: '0px !important',
    };
  }
  const minRenderedHeight =
    listsHeights.value[listId] > MIN_LIST_HEIGHT ? listsHeights.value[listId] : MIN_LIST_HEIGHT;
  return {
    minHeight: listsHeights.value[listId]
      ? `${minRenderedHeight}px !important`
      : `${getWrapperHeight(Number(allListCards.value.subgroupsLargestColumns[listId] ?? MIN_LIST_HEIGHT))}px !important`,
  };
};

const subGroupBy = computed(() => {
  return store.getters.getBoardSubGroupBy(currentBoardId.value);
});

watch(
  () => [boardGroupBy.value, subGroupBy.value],
  () => {
    listsHeights.value = {};
  }
);
</script>

<style lang="scss">
.swimlanes-wrap {
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  height: 100%;

  .empty-group {
    flex-basis: 0 !important;
    min-width: 0 !important;
    > * {
      display: none;
    }
  }
}
</style>
